import React from "react";
import { Eye, Pencil, Trash2, ClipboardList } from "lucide-react";
import { Button } from "src/components/ui/button";
import { Skeleton } from "src/components/ui/skeleton";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "src/components/ui/table";
import { PropertyStatusBadge } from "./PropertyStatusBadge";

function SkeletonRow() {
  return (
    <TableRow>
      {[40, 160, 120, 140, 100, 100, 80].map((w, i) => (
        <TableCell key={i}>
          <Skeleton className="h-4 rounded" style={{ width: w }} />
        </TableCell>
      ))}
    </TableRow>
  );
}

function EmptyState({ onNewRequirement }) {
  return (
    <TableRow>
      <TableCell colSpan={7}>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <ClipboardList className="h-10 w-10 text-slate-300 mb-3" />
          <p className="text-sm font-medium text-slate-600">
            No buyer requirements found
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Get started by adding your first buyer requirement
          </p>
          <Button size="sm" className="mt-4" onClick={onNewRequirement}>
            + Add New
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

export function BuyerRequirementsTable({
  requirements = [],
  loading,
  meta,
  page,
  onPageChange,
  onShow,
  onEdit,
  onDelete,
  onNewRequirement,
}) {
  const startRow = (page - 1) * (meta?.per_page || 20) + 1;
  const endRow = Math.min(
    startRow + (requirements.length - 1),
    meta?.total || 0
  );

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead className="w-[50px]">#</TableHead>
            <TableHead className="min-w-[160px]">Contact</TableHead>
            <TableHead className="min-w-[140px]">Locations</TableHead>
            <TableHead className="min-w-[160px]">Budget</TableHead>
            <TableHead className="w-[120px]">Prop. Types</TableHead>
            <TableHead className="w-[100px]">Status</TableHead>
            <TableHead className="w-[100px] text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
          ) : requirements.length === 0 ? (
            <EmptyState onNewRequirement={onNewRequirement} />
          ) : (
            requirements.map((item, idx) => (
              <TableRow key={item.id}>
                <TableCell className="text-xs text-slate-400">
                  {startRow + idx}
                </TableCell>

                <TableCell>
                  <p className="font-medium text-slate-900 text-[13px]">
                    {item.contact?.name || "—"}
                  </p>
                  {item.contact?.phone && (
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      {item.contact.phone}
                    </p>
                  )}
                </TableCell>

                <TableCell>
                  <p className="text-[13px] text-slate-700 capitalize">
                    {item.preferred_localities?.length > 0
                      ? item.preferred_localities.join(", ")
                      : item.city || "—"}
                  </p>
                </TableCell>

                <TableCell>
                  <p className="text-[13px] font-medium text-slate-800">
                    {item.min_budget && item.max_budget
                      ? `₹${Number(item.min_budget).toLocaleString()} - ₹${Number(
                          item.max_budget
                        ).toLocaleString()}`
                      : item.max_budget
                      ? `Up to ₹${Number(item.max_budget).toLocaleString()}`
                      : "—"}
                  </p>
                </TableCell>

                <TableCell>
                  <p className="text-[13px] text-slate-600 capitalize">
                    {item.property_types?.join(", ")?.replace(/_/g, " ") || "—"}
                  </p>
                </TableCell>

                <TableCell>
                  <PropertyStatusBadge status={item.status} />
                </TableCell>

                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onShow(item)}
                      title="View details"
                    >
                      <Eye className="h-3.5 w-3.5 text-slate-400 hover:text-blue-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit(item)}
                      title="Edit requirement"
                    >
                      <Pencil className="h-3.5 w-3.5 text-slate-400 hover:text-slate-700" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDelete(item)}
                      title="Delete requirement"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-red-500" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {!loading && requirements.length > 0 && (
        <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3">
          <p className="text-xs text-slate-500">
            Showing {startRow}–{endRow} of {meta?.total ?? 0} requirements
          </p>

          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
            >
              ← Prev
            </Button>
            <span className="px-3 text-xs text-slate-500">
              {page} / {meta?.total_pages ?? 1}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= (meta?.total_pages ?? 1)}
              onClick={() => onPageChange(page + 1)}
            >
              Next →
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
